<template>
  <div
    class="fixed top-0 left-0 z-10 h-screen w-screen bg-nucleus-lightestBlue overflow-auto"
  >
    <LoadingScreen :loaded="loaded" :message="loadingMessage"></LoadingScreen>

    <ErrorBox
      v-if="errorHeading || errorContent"
      :heading="errorHeading"
      :content="errorContent"
      :fixed="errorFixed"
      @closeError="
        errorHeading = '';
        errorContent = '';
      "
    ></ErrorBox>

    <ErrorBox
      v-if="warningHeading || warningContent"
      :heading="warningHeading"
      :content="warningContent"
      :fixed="true"
      :warning="true"
      @closeError="
        warningHeading = '';
        warningContent = '';
      "
    ></ErrorBox>

    <ModalDeleteCheck
      v-if="deleteCheck"
      :text="$t('global.defaults.session', 1)"
      @closeModal="deleteCheck = false"
      @deleteMe="deleteSession()"
    ></ModalDeleteCheck>

    <ModalConfirmation
      v-if="deleteCheckUser"
      :text="$t('pages.session.removeParticipant')"
      @yes="
        removeParticipant(selectedParticipant);
        deleteCheckUser = false;
      "
      @no="deleteCheckUser = false"
    ></ModalConfirmation>

    <div v-if="loaded">
      <HeaderMini
        :name="$t('global.defaults.session', 2)"
        :to="{ name: 'sessions' }"
        :can-delete="true"
        :can-edit="session.state !== 'FINISHED'"
        :no-menu="session.state === 'STARTED'"
        :text="$t('global.defaults.session', 1)"
        @deleteCheck="deleteCheck = true"
        @edit="edit()"
      ></HeaderMini>
      <HeaderShadow></HeaderShadow>

      <div class="px-4">
        <Button
          v-if="session.state === 'PENDING' || session.state === 'STARTING'"
          class="mt-4"
          :name="$t('global.defaults.startSession')"
          type="button"
          :loading="startingStopping"
          @click="startSession()"
        ></Button>
        <ButtonRed
          v-if="session.state === 'STARTED'"
          class="mt-4"
          type="button"
          :name="$t('global.defaults.endSession')"
          :loading="startingStopping"
          @click="endSession()"
        ></ButtonRed>
        <div class="my-4">
          <div class="mb-4">
            {{ $t("global.defaults.status") }}
            <span class="font-bold capitalize text-nucleus-primaryBlue">{{
              mapState(session.state)
            }}</span>
          </div>
          <div
            v-if="session.state === 'PENDING' || session.state === 'FAILED'"
            class="bg-nucleus-white p-2 text-nucleus-primaryBlue w-full"
            v-html="$t('pages.session.pendingMessage')"
          ></div>
          <div
            v-if="session.state === 'FINISHED'"
            class="bg-nucleus-white p-2 text-nucleus-primaryBlue w-full"
            v-html="$t('pages.session.finishedMessage')"
          ></div>
          <div
            v-if="session.state === 'STARTING'"
            class="bg-nucleus-white p-2 text-nucleus-primaryBlue w-full"
            v-html="$t('pages.session.startingMessage')"
          ></div>
          <div
            v-if="session.state === 'STARTED'"
            class="bg-nucleus-white p-2 text-nucleus-primaryBlue"
            v-html="$t('pages.session.startedMessage')"
          ></div>
        </div>
      </div>

      <div class="bg-nucleus-white">
        <div class="flex items-center px-4 py-6 border-b">
          <div
            class="text-3xl leading-none p-2 mr-6 flex self-center transition rounded-full icon-session"
            :class="getIconColour()"
          ></div>
          <div class="text-2xl">
            {{ session.name }}
          </div>
        </div>
        <div class="px-4 pt-6">
          <InfoItem
            :heading="$t('global.defaults.status')"
            :value="mapState(session.state)"
            class="capitalize"
          ></InfoItem>
          <InfoItem
            :heading="$t('global.defaults.headingName')"
            :value="session.name"
          ></InfoItem>
          <InfoItem
            :heading="$t('inputs.room.label')"
            :value="session.roomName"
          ></InfoItem>
          <InfoItem
            v-if="session.department"
            :heading="$t('global.defaults.headingDepartment')"
            :value="session.department"
          ></InfoItem>
          <InfoItem
            v-if="session.notes"
            :heading="$t('global.defaults.headingNotes')"
            :value="session.notes"
          ></InfoItem>
          <InfoItem
            v-if="session.createdOn"
            :heading="$t('global.defaults.headingDateCreated')"
            :value="getDate(session.createdOn)"
          ></InfoItem>
        </div>
      </div>
      <div v-if="session.invitees" class="p-4">
        <div class="mb-4">
          {{ $t("pages.session.selectedParticipants") }} ({{
            session.invitees.length
          }}/{{ sessionOptions.maxRemoteUsers }})
        </div>
        <div v-if="session.invitees.length < 1" class="text-center">
          {{ $t("pages.session.noParticipants") }}
        </div>
        <div v-if="session.invitees.length > 0">
          <ListItemBasic
            v-for="(user, index) in orderByName(session.invitees)"
            :key="user.userId"
            icon="remote-user"
            :name="user.name"
            :status="user.userStatus"
            :notes="getParticipantNotes(user)"
            class="cursor-pointer"
            @click="getParticipantDetails(index)"
          ></ListItemBasic>
        </div>
        <AddButtonBasic
          v-if="
            session.state !== 'FINISHED' &&
            session.invitees.length < sessionOptions.maxRemoteUsers
          "
          class="mt-4"
          :name="$t('buttons.addParticipant')"
          :icon="true"
          @click="toggleParticipants()"
        ></AddButtonBasic>
      </div>
    </div>
    <div
      v-if="showParticipantDetails"
      class="overflow-auto h-screen w-screen fixed top-0 left-0 bg-white"
    >
      <HeaderMiniBasic
        :name="selectedParticipant.name"
        @goBack="showParticipantDetails = false"
      ></HeaderMiniBasic>
      <HeaderShadow></HeaderShadow>

      <div class="bg-nucleus-white">
        <div class="flex items-center px-4 py-6 border-b">
          <div
            class="text-3xl leading-none p-2 mr-6 flex self-center transition bg-nucleus-primaryBlue text-nucleus-white rounded-full icon-remote-user"
          ></div>
          <div class="text-2xl">
            {{ $t("pages.session.participantDetails") }}
          </div>
        </div>
        <div class="px-4 pt-6">
          <InfoItem
            :heading="$t('global.defaults.headingAccountStatus')"
            :value="mapState(selectedParticipant.userStatus)"
          ></InfoItem>
          <InfoItem
            v-if="getParticipantNotes(selectedParticipant)"
            :heading="$t('global.defaults.headingSessionState')"
            :value="getParticipantNotes(selectedParticipant)"
          ></InfoItem>
          <InfoItem
            :heading="$t('global.defaults.headingName')"
            :value="selectedParticipant.name"
          ></InfoItem>
          <InfoItem
            :heading="$t('global.defaults.headingEmail')"
            :value="selectedParticipant.email"
          ></InfoItem>
          <InfoItem
            :heading="$t('global.defaults.headingMobilePhone')"
            :value="selectedParticipant.phone"
          ></InfoItem>
          <InfoItem
            v-if="selectedParticipant.affiliation"
            :heading="$t('global.defaults.headingAffiliation')"
            :value="selectedParticipant.affiliation"
          ></InfoItem>
          <InfoItem
            v-if="selectedParticipant.notes"
            :heading="$t('global.defaults.headingNotes')"
            :value="selectedParticipant.notes"
          ></InfoItem>
          <div
            v-if="session.state === 'STARTED' || session.state === 'STARTING'"
            class="cursor-pointer text-nucleus-primaryBlue underline mb-6"
          >
            <span @click="reinviteParticipant(selectedParticipant)">
              {{ $t("pages.session.reInvite") }}
            </span>
          </div>
          <div
            v-if="session.state !== 'FINISHED'"
            class="cursor-pointer text-nucleus-error underline text-sm"
          >
            <span @click="deleteCheckUser = true">
              {{ $t("pages.session.removeParticipantHeading") }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showParticipants"
      class="overflow-auto h-screen w-screen fixed top-0 left-0 bg-white p-4"
    >
      <div class="flex items-center justify-between mb-4">
        <div class="text-2xl">{{ $t("buttons.addParticipant") }}</div>
        <div
          class="text-3xl p-2 bg-nucleus-lightGreyMouseOver hover:bg-nucleus-primaryBlue hover:text-nucleus-white transition leading-none flex self-center cursor-pointer icon-close rounded-full"
          @click="toggleParticipants()"
        ></div>
      </div>

      <div class="pt-4 px-4">
        <Input
          id="filter"
          v-model:value="nameFilter"
          input-type="text"
          :label="$t('inputs.filter.label')"
          class="custom-bg"
          :max-length="inputMaxLengths.name"
          :filter="true"
          :placeholder="$t('inputs.filter.placeholder')"
          @input="filterByName(nameFilter)"
          @clearInput="clearInput()"
        />
      </div>

      <div v-if="users">
        <ListItemBasic
          v-for="user in users"
          :key="user.userId"
          icon="remote-user"
          :name="user.name"
          :status="user.status"
          :invite-state="user.state"
          :invite-reason="user.reason"
          class="cursor-pointer"
          :class="{
            'opacity-50 cursor-not-allowed': user.status !== 'VERIFIED',
          }"
          @click="addParticipant(user)"
        ></ListItemBasic>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderMini from "@/components/global/HeaderMini.vue"; // @ is an alias to /src
import HeaderMiniBasic from "@/components/global/HeaderMiniBasic.vue"; // @ is an alias to /src
import HeaderShadow from "@/components/utils/HeaderShadow.vue"; // @ is an alias to /src
import InfoItem from "@/components/utils/InfoItem.vue"; // @ is an alias to /src
import LoadingScreen from "@/components/global/LoadingScreen.vue";
import AddButtonBasic from "@/components/utils/AddButtonBasic.vue"; // @ is an alias to /src
import ListItemBasic from "@/components/utils/ListItemBasic.vue"; // @ is an alias to /src
import ButtonSecondary from "@/components/utils/ButtonSecondary.vue"; // @ is an alias to /src
import ButtonRed from "@/components/utils/ButtonRed.vue"; // @ is an alias to /src
import Button from "@/components/utils/Button.vue"; // @ is an alias to /src
import ModalConfirmation from "@/components/global/ModalConfirmation.vue";
import ModalDeleteCheck from "@/components/global/ModalDeleteCheck.vue";
import ErrorBox from "@/components/utils/ErrorBox.vue";
import { mapAwsUserStatus } from "@/utils/awsMappings";
import { getPrettyDateTime } from "@/utils/prettyDate";
import Input from "@/components/utils/Input.vue"; // @ is an alias to /src

import InputMaxLengths from "@/store/data/inputMaxLengths.json";
import { getLocalisedError } from "@/utils/apiUtils";

@Options({
  components: {
    HeaderMini,
    HeaderMiniBasic,
    HeaderShadow,
    InfoItem,
    LoadingScreen,
    ListItemBasic,
    AddButtonBasic,
    Button,
    ButtonSecondary,
    ButtonRed,
    ModalConfirmation,
    ModalDeleteCheck,
    ErrorBox,
    Input,
  },
  data() {
    return {
      session: {},
      sessionOptions: {
        maxRemoteusers: 0,
      },
      loaded: false,
      showParticipants: false,
      users: null,
      usersOriginal: null,
      startingStopping: false,
      interval: null,
      timeout: 10000,
      sessionStatusCount: 0,
      deleteCheck: false,
      deleteCheckUser: false,
      loadingMessage: this.$t("global.defaults.loadingSession"),
      selectedParticipant: {},
      showParticipantDetails: false,
      errorHeading: "",
      errorContent: "",
      warningHeading: "",
      warningContent: "",
      errorFixed: true,
      sessionTimeout: null,
      nameFilter: "",
      inputMaxLengths: InputMaxLengths[0],
    };
  },
  computed: {
    remoteUsers() {
      return this.$store.state.user.remoteUsers;
    },
  },
  beforeDestroy() {
    clearTimeout(this.sessionTimeout);
  },
  beforeRouteLeave() {
    clearTimeout(this.sessionTimeout);
  },
  beforeMount() {
    this.getSession();
  },
  mounted() {
    document.title =
      this.$t("global.defaults.session", 1) +
      " - " +
      this.$t("global.defaults.browserTitle");
  },
  methods: {
    getIconColour() {
      const text = this.session.state.toLowerCase();
      switch (text) {
        case "started":
          return "text-white bg-nucleus-primaryBlue";
        default:
          return "text-nucleus-text bg-nucleus-iconBg";
      }
    },
    mapState(state: string) {
      return this.$t(mapAwsUserStatus(state));
    },
    getDate(date: Date) {
      return getPrettyDateTime(date);
    },
    getParticipantNotes(user: Record<string, unknown>) {
      let state = user.state;
      switch (state) {
        case "PENDING":
          return this.$t("pages.session.invitePending");

        case "SENT":
          return this.$t("pages.session.inviteSent");

        case "JOINED":
          return this.$t("pages.session.inviteJoined");

        case "DECLINED":
          if (user.reason) {
            return (
              this.$t("pages.session.inviteDeclined") + " - " + user.reason
            );
          }
          return this.$t("pages.session.inviteDeclined");

        default:
          return "";
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getParticipantDetails(index: any) {
      this.selectedParticipant = this.session.invitees[index];
      this.showParticipantDetails = true;
    },
    getSession() {
      this.$store
        .dispatch("getSession", this.$route.params.sessionId)
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.session = response.data;
            this.getSessionOptions();
            this.loaded = true;
            if (this.session.state === "STARTED") this.startingStopping = false;
            if (this.session.state === "STARTING") {
              this.startingStopping = true;
              this.checkSessionStatus();
            }
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    startSession() {
      if (this.session.state === "PENDING") {
        this.startingStopping = true;
        this.$store
          .dispatch("startSession", this.session.id)
          .then((response: Record<string, unknown>) => {
            if (response) {
              console.log(response);
              this.checkSessionStatus();
            }
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .catch((error: any) => {
            this.loaded = true;
            this.startingStopping = false;
            this.errorHeading = "";
            this.errorContent = getLocalisedError(error);
          });
      }
    },
    endSession() {
      this.startingStopping = true;
      this.$store
        .dispatch("endSession", this.session.id)
        .then((response: Record<string, unknown>) => {
          if (response) {
            console.log(response);
            this.startingStopping = false;
            this.session.state = "FINISHED";
            this.getSession();
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loaded = true;
          this.startingStopping = false;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    checkSessionStatus() {
      if (this.sessionTimeout) clearTimeout(this.sessionTimeout);
      // console.log("count", this.sessionStatusCount);
      // console.log("interval time", this.timeout);

      if (this.session.state === "STARTED") {
        this.startingStopping = false;
        this.sessionStatusCount = 0;
      } else {
        if (this.session.state === "STARTING") this.timeout = 35000;
        if (this.session.state === "STARTING" && this.sessionStatusCount > 1)
          this.timeout = 5000;

        this.sessionTimeout = setTimeout(() => {
          this.sessionStatusCount++;
          this.getSession();
          if (this.session.state === "PENDING") this.checkSessionStatus();
        }, this.timeout);
      }
    },
    toggleParticipants() {
      this.showParticipants = this.showParticipants ? false : true;
      if (this.showParticipants) {
        this.formatUsers();
      } else {
        this.clearInput();
      }
    },
    getSessionOptions() {
      this.$store
        .dispatch(
          "getSessionOptions",
          this.$store.getters.getUserObject.attributes["custom:tenancy"]
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response.data) {
            this.sessionOptions = response.data;
            if (this.session.state === "PENDING") {
              const allowanceWarning = this.sessionOptions.allowanceWarning;
              if (allowanceWarning?.noCredit) {
                this.errorHeading = "";
                this.errorContent = this.$t("error.session.insufficientCredit");
              } else if (allowanceWarning?.show) {
                this.warningHeading = "";
                this.warningContent = this.$t("warning.session.lowCredit", {
                  credits: allowanceWarning.threshold,
                });
              }
            }
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.errorFixed = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    getUsers() {
      this.loaded = false;
      this.loadingMessage = this.$t("global.defaults.loading");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.$store
        .dispatch("getRemoteUsers")
        .then((response: any) => {
          if (response) {
            this.loaded = true;
            this.formatUsers();
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    formatUsers() {
      if (this.remoteUsers[0]) {
        var userIds: any[] = [];

        this.session.invitees.forEach(function (user: any) {
          userIds.push(user.userId);
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.users = this.remoteUsers.filter((user: any) => {
          return !userIds.includes(user.userId);
        });

        this.users = this.orderByName(this.users);
        this.usersOriginal = this.users;
      } else {
        this.getUsers();
      }
    },
    addParticipant(user: Record<string, unknown>) {
      if (user.status === "VERIFIED") {
        this.loaded = false;
        this.loadingMessage = this.$t("pages.session.addingParticipant");
        const data = {
          userId: user.userId,
        };

        this.$store
          .dispatch("addUserToSession", {
            sessionId: this.session.id,
            data: data,
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .then((response: any) => {
            if (response) {
              this.toggleParticipants();
              this.getSession();
              // console.log("add user to session", response);
            }
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .catch((error: any) => {
            this.toggleParticipants();
            this.loaded = true;
            this.errorHeading = "";
            this.errorContent = getLocalisedError(error);
          });
      }
    },
    removeParticipant(user: Record<string, unknown>) {
      this.loaded = false;
      this.loadingMessage = this.$t("pages.session.removingParticipant");
      const data = {
        userId: user.userId,
      };

      this.$store
        .dispatch("removeUserFromSession", {
          sessionId: this.session.id,
          data: data,
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response) {
            this.showParticipantDetails = false;
            this.getSession();
            // console.log("remove user from session", response);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.showParticipantDetails = false;
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },

    edit() {
      this.$router.push({
        name: "sessionsEdit",
        params: { sessionId: this.$route.params.sessionId },
      });
    },
    deleteSession() {
      this.loaded = false;
      this.loadingMessage = this.$t("global.defaults.deletingSession");
      this.$store
        .dispatch("deleteSession", this.$route.params.sessionId)
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.loaded = true;
            console.log(response.data);
            this.$store.dispatch("changeUpdate", "session");
            this.$router.push({ name: "sessions" });
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    reinviteParticipant(user: Record<string, unknown>) {
      this.loaded = false;
      this.loadingMessage = this.$t("pages.session.reInviting");

      const data = {
        userId: user.userId,
      };

      this.$store
        .dispatch("reinviteUserToSession", {
          sessionId: this.session.id,
          data: data,
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response) {
            this.showParticipantDetails = false;
            this.getSession();
            // console.log("reinvite user to session", response);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.showParticipantDetails = false;
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    toLowerCase(text: string) {
      return text.toLowerCase();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    orderByName(object: Array<any>) {
      return object.sort((a, b) => a.name.localeCompare(b.name));
    },
    filterByName(name: string) {
      console.log(name);
      this.users = this.usersOriginal.filter((user: any) =>
        user.name.toLowerCase().includes(name.toLowerCase())
      );
    },
    clearInput() {
      this.nameFilter = "";
      this.filterByName(this.nameFilter);
    },
  },
})
export default class SessionsDetail extends Vue {}
</script>
